import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import {
    PhysicalUnit, WeeklyPlan, ProtocolConfig, CompanyConfig, ProtocolState, Contract, PaymentStatement,
    ProjectInfo, Responsible, Profile, Activity, Task, ProjectProgressSnapshot, LookaheadPlan
} from '../src/types';
import {
    calculateSCurve, SCurvePoint, calculateWeightedProgress,
    calculateProjectProgress, truncateProgress, calculateDelayDays
} from '../src/utils/planningUtils';

import { NotificationBell } from '../src/components/ui/NotificationBell';

interface DashboardScreenProps {
    projectInfo: ProjectInfo;
    tasks: Task[];
    responsibles: Responsible[];
    savedProtocols: Record<string, ProtocolState>;
    contracts: Contract[];
    paymentStatements: PaymentStatement[];
    activities: Activity[];
    progressHistory: ProjectProgressSnapshot[];
    units: PhysicalUnit[];
    matrixData: Record<string, any>;
    weeklyPlans: WeeklyPlan[];
    protocolConfigs: Record<string, ProtocolConfig>;
    companyConfig: CompanyConfig;
    lookaheadPlans: LookaheadPlan[];
    notifications?: any;
    setNotifications?: any;
    currentUser?: any;
    hasPermission?: (module: string, action: 'view'|'edit') => boolean;
    subcontractors?: any[];
    isNative?: boolean;
}

const DashboardScreen: React.FC<DashboardScreenProps> = ({
    projectInfo, tasks, responsibles, savedProtocols, contracts, paymentStatements,
    activities, progressHistory, units, matrixData, weeklyPlans, protocolConfigs, companyConfig, lookaheadPlans,
    notifications, setNotifications, currentUser, hasPermission = () => true, subcontractors = [], isNative = false
}) => {
    const navigate = useNavigate();
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [selectedMobileSubcontractorId, setSelectedMobileSubcontractorId] = useState<string>('');

    React.useEffect(() => {
        if (!selectedMobileSubcontractorId && contracts.length > 0) {
            const availableSubIds = Array.from(new Set(contracts.filter(c => c.status !== 'Anulado').map(c => c.subcontractorId)))
                .filter(subId => {
                    if (currentUser?.globalRole === 'Administrador') return true;
                    const sub = subcontractors.find(s => s.id === subId);
                    return sub?.linkedUserIds?.includes(currentUser?.id || '');
                })
                .sort((a, b) => {
                    const subA = subcontractors.find(s => s.id === a);
                    const subB = subcontractors.find(s => s.id === b);
                    return (subA?.razonSocial || '').localeCompare(subB?.razonSocial || '');
                });

            if (availableSubIds.length > 0) {
                setSelectedMobileSubcontractorId(availableSubIds[0]);
            }
        }
    }, [contracts, subcontractors, currentUser, selectedMobileSubcontractorId]);

    const downloadPDF = async () => {
        const input = document.getElementById('dashboard-content');
        if (!input) return;
        setIsGeneratingPdf(true);
        try {
            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const margin = 25.4; // 25.4 mm (1 pulgada, estándar de impresión)
            const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            
            pdf.addImage(imgData, 'PNG', margin, margin, pdfWidth, pdfHeight);
            pdf.save(`Dashboard_${projectInfo?.name?.replace(/\s+/g, '_') || 'K-LyDapp'}.pdf`);
        } catch (error) {
            console.error('Error generando PDF', error);
            alert('Hubo un error al generar el PDF. Asegúrate de intentar nuevamente.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };
    // --- Calculations ---

    // 1. Project Progress (Weighted Average)
    // 1. Project Progress (Use last synchronized history)
    const projectProgress = useMemo(() => {
        if (progressHistory && progressHistory.length > 0) {
            let validHistory = progressHistory.filter(h => h && h.date);
            const todayStr = new Date().toISOString().split('T')[0];
            
            validHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));

            if (validHistory.length > 0) {
                return validHistory[validHistory.length - 1].progress || 0;
            }
        }
        return 0; // Fallback to 0 if no history
    }, [progressHistory]);

    // 2. Subcontracts Health
    const activeSubcontracts = contracts.filter(c => c.status === 'Activo').length;
    const totalContractedAmount = contracts.filter(c => c.status !== 'Anulado').reduce((sum, c) => sum + (c.totalAmount || 0), 0);
    const totalPaidAmount = paymentStatements
        .filter(ps => ps.status === 'Aprobado' || ps.status === 'Autorizado')
        .reduce((sum, ps) => {
            const amtStr = ps.amount || '0';
            return sum + (parseFloat(amtStr.replace(/\./g, "").replace(/[^0-9-]/g, "")) || 0);
        }, 0);

    // 3. Quality Metrics
    const qualityMetrics = useMemo(() => {
        const stats = { open: 0, reviewing: 0, observations: 0, closed: 0 };
        (Object.values(savedProtocols) as ProtocolState[]).forEach(p => {
            if (p.status === 'Abierto' || p.status === 'Resuelto') stats.open++;
            else if (p.status === 'Rechazado') stats.observations++;
            else if (p.status === 'Cerrado') stats.closed++;

            // "In Review" logic: if it's open or resolved but has at least one signature
            if ((p.status === 'Abierto' || p.status === 'Resuelto') && Object.keys(p.signatures || {}).length > 0) {
                stats.reviewing++;
                stats.open--;
            }
        });
        return stats;
    }, [savedProtocols]);

    // 4. S-Curve Data (Real calculation based on tasks)
    const curveData = useMemo(() => {
        const virtualHistory = [...(progressHistory || [])];
        const todayStr = new Date().toISOString().split('T')[0];
        
        return calculateSCurve(tasks, virtualHistory);
    }, [tasks, progressHistory]);

    // Custom Tooltip for S-Curve
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload as SCurvePoint;

            // Find delay data for this date in progressHistory
            const syncRecord = progressHistory.find(h => h.date === data.date);

            return (
                <div className="bg-[#1e293b] p-3 rounded-lg border border-slate-700 shadow-xl text-white text-[11px] flex flex-col gap-1.5 min-w-[180px]">
                    <div className="flex justify-between items-center border-b border-slate-700 pb-1.5 mb-0.5">
                        <span className="font-bold text-slate-300">Semana {data.weekIndex}</span>
                        <span className="text-[10px] text-slate-400">({new Date(data.date + "T12:00:00").toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })})</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-400">Programado:</span>
                        <span className="font-bold text-slate-200">{truncateProgress(data.plan)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-400">Avance Real:</span>
                        <span className="font-bold text-blue-400">{data.real !== null ? `${truncateProgress(data.real)}%` : '--'}</span>
                    </div>
                    {syncRecord && (
                        <>
                            <div className="h-[1px] bg-slate-700 my-0.5" />
                            <div className="flex justify-between items-center">
                                <span className="text-slate-400">Atraso Ruta Crítica:</span>
                                <span className={`font-bold ${syncRecord.delayRutaCritica && syncRecord.delayRutaCritica > 0 ? 'text-orange-400' : 'text-slate-300'}`}>
                                    {syncRecord.delayRutaCritica || 0} días
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-slate-400">Atraso Curva S:</span>
                                <span className={`font-bold ${syncRecord.delayCurvaS && syncRecord.delayCurvaS > 0 ? 'text-red-400' : 'text-slate-300'}`}>
                                    {syncRecord.delayCurvaS || 0} días
                                </span>
                            </div>
                        </>
                    )}
                </div>
            );
        }
        return null;
    };

    // 5. Project Delay Status (Calculado Dinámicamente "En Vivo" al día de hoy)
    const projectStatus = useMemo(() => {
        if (!tasks || tasks.length === 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay: 0 };
        }
        
        // 1. Calcular el progreso acumulado real actual en vivo
        const liveProgress = calculateProjectProgress(
            tasks,
            units,
            matrixData,
            protocolConfigs,
            projectInfo,
            savedProtocols,
            weeklyPlans
        );

        // 2. Obtener la fecha de hoy en huso horario local
        const todayStr = new Date().toISOString().split('T')[0];

        // 3. Calcular la desviación dinámica en base al progreso en vivo
        const calculatedDelay = calculateDelayDays(curveData, liveProgress, todayStr);

        // 4. Considerar también la ruta crítica si existen snapshots previos
        let delayRutaCritica = 0;
        if (progressHistory && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                const lastRecord = [...validHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
                delayRutaCritica = lastRecord.delayRutaCritica || 0;
            }
        }

        const delay = Math.max(calculatedDelay, delayRutaCritica);

        if (delay <= 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay: 0 };
        } else if (delay <= 9) {
            return { label: 'En Riesgo', color: 'bg-[#f4d03f] border-[#d4ac0d] text-slate-900', icon: 'warning', delay };
        } else {
            return { label: 'Atrasado', color: 'animate-pulse bg-[#ff0000] border-[#cc0000] text-yellow-300', icon: 'error', delay };
        }
    }, [tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, curveData, progressHistory]);

    // --- MOBILE METRICS ---
    const lookaheadMetrics = useMemo(() => {
        let open = 0;
        let closed = 0;
        let pcr = 0;
        let planNumber = 0;
        if (lookaheadPlans && Array.isArray(lookaheadPlans) && lookaheadPlans.length > 0) {
            const sortedPlans = [...lookaheadPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
            const activePlan = sortedPlans[0];
            if (activePlan) {
                const chronologicalPlans = [...lookaheadPlans].sort((a,b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                const index = chronologicalPlans.findIndex(p => p.id === activePlan.id);
                planNumber = index !== -1 ? index + 1 : lookaheadPlans.length;
                
                if (Array.isArray(activePlan.constraints)) {
                    activePlan.constraints.forEach(c => {
                        if (c.status === 'Liberado') closed++;
                        else open++;
                    });
                }
                const total = open + closed;
                pcr = total > 0 ? Math.round((closed / total) * 100) : 100;
            }
        }
        return { open, closed, pcr, planNumber };
    }, [lookaheadPlans]);

    const weeklyMetrics = useMemo(() => {
        let executed = 0;
        let pending = 0;
        let ppc = 0;
        let planNumber = 0;
        if (weeklyPlans && Array.isArray(weeklyPlans) && weeklyPlans.length > 0) {
            const sortedPlans = [...weeklyPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
            const activeWeekly = sortedPlans.find(wp => wp && wp.status === 'En Ejecución') || sortedPlans.find(wp => wp && wp.status === 'Cerrada') || sortedPlans[0];
            if (activeWeekly) {
                planNumber = activeWeekly.id;
                if (Array.isArray(activeWeekly.allocations)) {
                    activeWeekly.allocations.forEach(alloc => {
                        if (alloc.executed) executed++;
                        else pending++;
                    });
                }
                const total = executed + pending;
                ppc = total > 0 ? Math.round((executed / total) * 100) : 0;
            }
        }
        return { executed, pending, ppc, planNumber };
    }, [weeklyPlans]);

    const currentPayment = useMemo(() => {
        if (!paymentStatements || !Array.isArray(paymentStatements)) return '$0';
        const sorted = [...paymentStatements].filter(ps => ps && ps.status !== 'Anulado' && ps.startDate).sort((a,b) => new Date(b.startDate || '').getTime() - new Date(a.startDate || '').getTime());
        if (sorted.length > 0) {
             const amt = sorted[0].amount;
             const parsed = parseFloat(typeof amt === 'string' ? amt.replace(/\./g, "").replace(/[^0-9-]/g, "") : String(amt || 0));
             return formatCurrency(parsed || 0);
        }
        return '$0';
    }, [paymentStatements]);

    const latestProgressRecord = useMemo(() => {
        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                return [...validHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
            }
        }
        return null;
    }, [progressHistory]);
    const delayCS = latestProgressRecord?.delayCurvaS || 0;

    const mobileSubcontractorMetrics = useMemo(() => {
        let totalContracted = 0;
        let totalPaid = 0;
        let currentEP = '$0';
        
        if (selectedMobileSubcontractorId) {
            const subContracts = contracts.filter(c => c.subcontractorId === selectedMobileSubcontractorId && c.status !== 'Anulado');
            totalContracted = subContracts.reduce((sum, c) => sum + (c.totalAmount || 0), 0);
            
            const subEPs = paymentStatements.filter(ps => ps.subcontractorId === selectedMobileSubcontractorId && (ps.status === 'Aprobado' || ps.status === 'Autorizado'));
            totalPaid = subEPs.reduce((sum, ps) => {
                const amtStr = ps.amount || '0';
                return sum + (parseFloat(amtStr.replace(/\./g, "").replace(/[^0-9-]/g, "")) || 0);
            }, 0);
            
            let totalPayable = 0;
            subContracts.forEach(contract => {
                const validEPs = paymentStatements.filter(ep => ep.contractId === contract.id && ep.status !== 'Anulado' && ep.status !== 'Borrador');
                const consolidatedItemsMap = new Map();
                (contract.items || []).forEach(item => consolidatedItemsMap.set(item.id, JSON.parse(JSON.stringify(item))));
                (contract.anexos || []).filter(a => a.status === 'Activo').forEach(anexo => {
                    (anexo.items || []).forEach(aItem => {
                        if (consolidatedItemsMap.has(aItem.id)) {
                            const existing = consolidatedItemsMap.get(aItem.id);
                            aItem.details.forEach(d => {
                                const eIdx = existing.details.findIndex(ed => ed.unitId === d.unitId);
                                if (eIdx >= 0) existing.details[eIdx] = d; else existing.details.push(d);
                            });
                            existing.totalValue = existing.details.reduce((sum, d) => sum + d.value, 0);
                        } else {
                            consolidatedItemsMap.set(aItem.id, JSON.parse(JSON.stringify(aItem)));
                        }
                    });
                });

                consolidatedItemsMap.forEach(item => {
                    (item.details || []).forEach(detail => {
                        const isAlreadyPaid = validEPs.some(ep => 
                            ep.items?.some(i => i.id === item.id && i.selectedUnitIds?.includes(detail.unitId))
                        );
                        
                        if (!isAlreadyPaid) {
                            const isPayable = Object.values(savedProtocols).some((p: ProtocolState) => {
                                if (p.taskId === item.id && p.unitId === detail.unitId) {
                                    const config = protocolConfigs[item.id];
                                    const group = p.groupId
                                        ? config?.reviewerGroups?.find(g => g.id === p.groupId)
                                        : config?.reviewerGroups?.find(g => g.unitIds.includes(p.unitId));

                                    if (group) {
                                        return group.reviewers.some((rev, idx) =>
                                            rev.generatesPayment && p.signatures[idx]
                                        );
                                    }
                                }
                                return false;
                            });
                            if (isPayable) {
                                totalPayable += detail.value;
                            }
                        }
                    });
                });
            });
            currentEP = formatCurrency(totalPayable);
        }
        
        return { totalContracted: formatCurrency(totalContracted), totalPaid: formatCurrency(totalPaid), currentEP };
    }, [contracts, paymentStatements, selectedMobileSubcontractorId, savedProtocols, protocolConfigs]);


    function formatCurrency(val: number) {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    }

    return (
        <div className="flex flex-1 flex-col h-full overflow-hidden relative">
            <header className={`${isNative ? 'hidden' : 'hidden md:flex'} h-16 w-full items-center justify-between border-b border-[#e7ecf3] bg-white px-6 dark:bg-[#1a2634] dark:border-[#2a3644] flex-shrink-0`}>
                <div className="flex items-center gap-4">
                    <div className="flex w-96 max-w-lg items-center rounded-lg bg-[#f3f4f6] dark:bg-[#2a3644] px-3 py-2">
                        <span className="material-symbols-outlined text-slate-400 text-[20px]">search</span>
                        <input className="ml-2 w-full bg-transparent text-sm text-slate-900 placeholder-slate-400 focus:outline-none dark:text-white border-none focus:ring-0" placeholder="Buscar proyectos, tareas..." type="text" />
                    </div>
                </div>
                
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3 border-r border-slate-200 dark:border-slate-700 pr-6 mr-2">
                        <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt={companyConfig?.name || 'Constructora LD'} className="hidden md:block h-8 w-auto object-contain" />
                        <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden h-8 w-auto object-contain rounded-md" />
                        <h2 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase leading-none">{companyConfig?.name}</h2>
                    </div>

                    <div className="flex items-center gap-4">
                        {notifications && setNotifications && (
                            <NotificationBell notifications={notifications} setNotifications={setNotifications} currentUser={currentUser} />
                        )}
                        <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1"></div>
                    </div>
                </div>
            </header>
            <main className="flex-1 overflow-y-auto bg-background-light dark:bg-background-dark p-4 md:p-6 lg:p-10">
                <div id="dashboard-content" className="mx-auto max-w-7xl flex flex-col gap-6 md:gap-8 pb-10 bg-background-light dark:bg-background-dark">
                    
                    {/* MOBILE: Módulos Stack REMOVED */}


                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <span className="hover:text-primary transition-colors cursor-pointer">Proyectos</span>
                            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                            <span className="hover:text-primary transition-colors cursor-pointer">{projectInfo.name}</span>
                            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                            <span className="font-medium text-slate-900 dark:text-white">Dashboard</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                                    <span className="md:hidden">Panel de Control</span>
                                    <span className="hidden md:inline">Resumen de la Obra</span>
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold shadow-sm ${projectStatus.color}`}>
                                        <span className="material-symbols-outlined text-[18px]">{projectStatus.icon}</span>
                                        {projectStatus.label} {projectStatus.delay > 0 && `(${projectStatus.delay}d desvío)`}
                                    </div>
                                </h1>
                                <p className="text-slate-500 dark:text-slate-400 mt-1">
                                    <span className="md:hidden">Accesos directos y estado de tareas</span>
                                    <span className="hidden md:inline">Avance consolidado de obra</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={downloadPDF} disabled={isGeneratingPdf} className={`inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${isGeneratingPdf ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700' : 'bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#1a2634] dark:text-slate-200 dark:hover:bg-[#253242]'}`}>
                                    <span className={`material-symbols-outlined text-[20px] ${isGeneratingPdf ? 'animate-spin' : ''}`}>
                                        {isGeneratingPdf ? 'sync' : 'cloud_download'}
                                    </span>
                                    {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
                                </button>
                                
                                {/* Mobile triggers inline next to Descargar PDF */}
                                <div className="flex md:hidden items-center gap-1.5 ml-1">
                                    <button 
                                        onClick={() => window.dispatchEvent(new CustomEvent('open-bug-report'))}
                                        className="w-10 h-10 bg-rose-600 border border-rose-500/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                                        title="Reportar Falla"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">bug_report</span>
                                    </button>
                                    <button 
                                        onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
                                        className="w-10 h-10 bg-white border border-slate-200 text-slate-800 rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95 dark:bg-[#1a2634] dark:border-slate-700 dark:text-white"
                                        title="Asistente IA"
                                    >
                                        <svg viewBox="0 0 100 100" className="w-[20px] h-[20px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M25 50 C25 25, 75 25, 75 50" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" />
                                            <rect x="18" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                            <rect x="74" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                            <circle cx="40" cy="50" r="4.5" fill="currentColor" />
                                            <circle cx="60" cy="50" r="4.5" fill="currentColor" />
                                            <path d="M42 63 C45 67, 55 67, 58 63" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                                            <path d="M78 56 C78 72, 68 82, 52 82" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                        
                        {/* MOBILE EXCLUSIVE: Planificación Intermedia */}
                        {hasPermission("panel_intermedia", "view") && (
                        <div onClick={() => navigate('/planning')} className="md:hidden cursor-pointer group rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#1f2937] p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/50 dark:bg-blue-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center border border-blue-100 dark:border-blue-500/20">
                                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px]">visibility</span>
                                </div>
                                <div>
                                    <h3 className="text-[15px] font-black text-slate-800 dark:text-white leading-tight group-hover:text-blue-600 transition-colors">Planificación Intermedia #{lookaheadMetrics.planNumber || '-'}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{lookaheadMetrics.pcr}% PCR</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                                <div className="flex flex-col p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-orange-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span> Abiertas</span>
                                    <span className="text-lg font-black text-slate-700 dark:text-slate-200 mt-1 pl-3">{lookaheadMetrics.open}</span>
                                </div>
                                <div className="flex flex-col p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-emerald-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> Cerradas</span>
                                    <span className="text-lg font-black text-slate-700 dark:text-slate-200 mt-1 pl-3">{lookaheadMetrics.closed}</span>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* MOBILE EXCLUSIVE: Planificación Semanal */}
                        {hasPermission("panel_semanal", "view") && (
                        <div onClick={() => navigate('/planning')} className="md:hidden cursor-pointer group rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#1f2937] p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50/50 dark:bg-indigo-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center border border-indigo-100 dark:border-indigo-500/20">
                                    <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400 text-[20px]">checklist</span>
                                </div>
                                <div>
                                    <h3 className="text-[15px] font-black text-slate-800 dark:text-white leading-tight group-hover:text-indigo-600 transition-colors">Planificación Semanal #{weeklyMetrics.planNumber || '-'}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{weeklyMetrics.ppc}% PPC</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                                <div className="flex flex-col p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-emerald-600 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-600"></span> Finalizadas</span>
                                    <span className="text-lg font-black text-slate-700 dark:text-slate-200 mt-1 pl-3">{weeklyMetrics.executed}</span>
                                </div>
                                <div className="flex flex-col p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-orange-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span> Pendientes</span>
                                    <span className="text-lg font-black text-slate-700 dark:text-slate-200 mt-1 pl-3">{weeklyMetrics.pending}</span>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* MOBILE EXCLUSIVE: Estado Pago / Subcontratos */}
                        {hasPermission('panel_ep', 'view') && (
                        <div onClick={() => navigate('/payments')} className="md:hidden cursor-pointer group rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#1f2937] p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50/50 dark:bg-emerald-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center border border-emerald-100 dark:border-emerald-500/20 shrink-0">
                                    <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-[20px]">payments</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-[15px] font-black text-slate-800 dark:text-white leading-tight group-hover:text-emerald-600 transition-colors">Subcontratos</h3>
                                    <select 
                                        onClick={(e) => e.stopPropagation()} 
                                        value={selectedMobileSubcontractorId} 
                                        onChange={(e) => setSelectedMobileSubcontractorId(e.target.value)}
                                        className="mt-1 block w-full text-[10px] md:text-xs border-slate-200 dark:border-slate-600 rounded text-slate-600 dark:text-slate-300 p-1.5 bg-slate-50 dark:bg-slate-800 outline-none focus:ring-1 focus:ring-emerald-500 truncate"
                                    >
                                        <option value="">Seleccionar Subcontrato...</option>
                                        {Array.from(new Set(contracts.filter(c => c.status !== 'Anulado').map(c => c.subcontractorId)))
                                            .filter(subId => {
                                                if (currentUser?.globalRole === 'Administrador') return true;
                                                const sub = subcontractors.find(s => s.id === subId);
                                                return sub?.linkedUserIds?.includes(currentUser?.id || '');
                                            })
                                            .sort((a, b) => {
                                                const subA = subcontractors.find(s => s.id === a);
                                                const subB = subcontractors.find(s => s.id === b);
                                                return (subA?.razonSocial || '').localeCompare(subB?.razonSocial || '');
                                            })
                                            .map(subId => {
                                             const sub = subcontractors.find(s => s.id === subId);
                                             return <option key={subId} value={subId}>{sub?.razonSocial || 'Desconocido'}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>
                            
                            {selectedMobileSubcontractorId ? (
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                        <span className="text-[9px] font-bold text-slate-500 uppercase">Contratado</span>
                                        <span className="text-sm font-black text-slate-800 dark:text-slate-100 tracking-tight mt-0.5">{mobileSubcontractorMetrics.totalContracted}</span>
                                    </div>
                                    <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                        <span className="text-[9px] font-bold text-blue-600 uppercase">Pagado</span>
                                        <span className="text-sm font-black text-blue-600 tracking-tight mt-0.5">{mobileSubcontractorMetrics.totalPaid}</span>
                                    </div>
                                    <div className="col-span-2 flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                        <span className="text-[9px] font-bold text-emerald-600 uppercase flex items-center gap-1"><span className="material-symbols-outlined text-[12px]">receipt_long</span> ESTADO DE PAGO ACTUAL</span>
                                        <span className="text-base font-black text-emerald-600 tracking-tight mt-0.5">{mobileSubcontractorMetrics.currentEP}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col justify-center p-4 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700 mt-1">
                                    <span className="text-[10px] font-medium text-slate-500 uppercase text-center flex flex-col items-center gap-2">
                                        <span className="material-symbols-outlined text-slate-400 text-2xl">ads_click</span>
                                        Seleccione un subcontrato para ver sus montos
                                    </span>
                                </div>
                            )}
                        </div>
                        )}

                        {/* 1. Project Progress */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Avance de la Obra</p>
                                    <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{truncateProgress(projectProgress)}%</h3>
                                </div>
                                <div className="rounded-full bg-blue-50 p-2 dark:bg-blue-900/20">
                                    <span className="material-symbols-outlined text-primary text-[24px]">timelapse</span>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-col gap-2">
                                <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                    <div className="absolute h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${projectProgress}%` }}></div>
                                </div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Basado en Planificación Maestra</p>
                            </div>
                        </div>

                        {/* 2. Subcontracts */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Subcontratos Activos</p>
                                    <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{activeSubcontracts}</h3>
                                </div>
                                <div className="rounded-full bg-emerald-50 p-2 dark:bg-emerald-900/20">
                                    <span className="material-symbols-outlined text-emerald-600 text-[24px]">handshake</span>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-col gap-1 text-[11px]">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Contratado:</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-300">{formatCurrency(totalContractedAmount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Pagado:</span>
                                    <span className="font-bold text-emerald-600">{formatCurrency(totalPaidAmount)}</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1 mt-1">
                                    <span className="text-slate-500">% Pagado:</span>
                                    <span className="font-bold text-blue-600">
                                        {totalContractedAmount > 0 ? Math.round((totalPaidAmount / totalContractedAmount) * 100) : 0}%
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* MOBILE EXCLUSIVE: Control de Calidad */}
                        {hasPermission("panel_protocolos", "view") && (
                        <div onClick={() => navigate('/quality')} className="md:hidden cursor-pointer group rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#1f2937] p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-orange-50/50 dark:bg-orange-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center border border-orange-100 dark:border-orange-500/20 shrink-0">
                                    <span className="material-symbols-outlined text-orange-600 dark:text-orange-400 text-[20px]">fact_check</span>
                                </div>
                                <div>
                                    <h3 className="text-[15px] font-black text-slate-800 dark:text-white leading-tight group-hover:text-orange-600 transition-colors">Control de Calidad</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{(Object.values(qualityMetrics) as number[]).reduce((a, b) => a + b, 0)} Protocolos</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-blue-600 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span> Abiertos</span>
                                    <span className="text-base font-black text-slate-800 dark:text-slate-100 mt-1 pl-3">{qualityMetrics.open}</span>
                                </div>
                                <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-orange-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span> En Rev.</span>
                                    <span className="text-base font-black text-slate-800 dark:text-slate-100 mt-1 pl-3">{qualityMetrics.reviewing}</span>
                                </div>
                                <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-red-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-red-500"></span> Obs.</span>
                                    <span className="text-base font-black text-slate-800 dark:text-slate-100 mt-1 pl-3">{qualityMetrics.observations}</span>
                                </div>
                                <div className="flex flex-col justify-center p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-emerald-500 uppercase flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> Cerrados</span>
                                    <span className="text-base font-black text-slate-800 dark:text-slate-100 mt-1 pl-3">{qualityMetrics.closed}</span>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* 3. Quality Control (DESKTOP) */}
                        {(hasPermission("cc_config", "view") || hasPermission("mod_movil_calidad", "view")) && (
                        <div onClick={() => navigate('/quality')} className="hidden md:block cursor-pointer group rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634] hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Control de Calidad</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{(Object.values(qualityMetrics) as number[]).reduce((a, b) => a + b, 0)} Protocolos</h3>
                                </div>
                                <div className="rounded-full bg-orange-50 p-2 dark:bg-orange-900/20">
                                    <span className="material-symbols-outlined text-orange-600 text-[24px]">fact_check</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Abiertos</span>
                                    <span className="text-sm font-bold text-blue-600">{qualityMetrics.open}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">En Revisión</span>
                                    <span className="text-sm font-bold text-orange-500">{qualityMetrics.reviewing}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Obs.</span>
                                    <span className="text-sm font-bold text-red-500">{qualityMetrics.observations}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Cerrados</span>
                                    <span className="text-sm font-bold text-green-600">{qualityMetrics.closed}</span>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* 4. Resumen Planificación Semanal */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Planificación Semanal {weeklyMetrics.planNumber ? `#${weeklyMetrics.planNumber}` : ''}</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                                        {weeklyMetrics.ppc}% PPC
                                    </h3>
                                </div>
                                <div className="rounded-full bg-indigo-50 p-2 dark:bg-indigo-900/20">
                                    <span className="material-symbols-outlined text-indigo-600 text-[24px]">checklist</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Finalizadas</span>
                                    <span className="text-sm font-bold text-emerald-600">{weeklyMetrics.executed}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Pendientes</span>
                                    <span className="text-sm font-bold text-orange-500">{weeklyMetrics.pending}</span>
                                </div>
                            </div>
                        </div>

                        {/* 5. Resumen Planificación Intermedia */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Planificación Intermedia {lookaheadMetrics.planNumber ? `#${lookaheadMetrics.planNumber}` : ''}</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{lookaheadMetrics.pcr}% PCR</h3>
                                </div>
                                <div className="rounded-full bg-blue-50 p-2 dark:bg-blue-900/20">
                                    <span className="material-symbols-outlined text-blue-600 text-[24px]">visibility</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Liberadas</span>
                                    <span className="text-sm font-bold text-emerald-500">{lookaheadMetrics.closed}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Pendientes</span>
                                    <span className="text-sm font-bold text-orange-500">{lookaheadMetrics.open}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        {/* Curva S - HIDDEN ON MOBILE */}
                        <div className="hidden md:flex xl:col-span-2 flex-col gap-6">
                            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                                <div className="mb-6 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Curva S de Avance</h3>
                                        <p className="text-xs text-slate-500 italic">Programado vs Real Cumulativo</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                            <span className="h-2.5 w-2.5 rounded-full bg-slate-300"></span> Progr.
                                        </span>
                                        <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                            <span className="h-2.5 w-2.5 rounded-full bg-primary"></span> Real
                                        </span>
                                    </div>
                                </div>
                                <div className="h-72 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={curveData}>
                                            <defs>
                                                <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.1} />
                                                    <stop offset="95%" stopColor="#1a73e8" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis
                                                dataKey="name"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                                minTickGap={30}
                                            />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="plan" stroke="#cbd5e1" strokeWidth={2} fill="transparent" />
                                            <Area type="monotone" dataKey="real" stroke="#1a73e8" strokeWidth={3} fillOpacity={1} fill="url(#colorReal)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="hidden md:block xl:col-span-1">
                            <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#1a2634] flex flex-col">
                                <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Actividad Reciente</h3>
                                </div>
                                <div className="p-6 flex-1 overflow-y-auto max-h-[400px]">
                                    {activities.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                                            <span className="material-symbols-outlined text-4xl mb-2">history</span>
                                            <p className="text-sm italic text-center">No hay actividad registrada aún en el proyecto.</p>
                                        </div>
                                    ) : (
                                        <ol className="relative border-l border-slate-200 dark:border-slate-700">
                                            {activities.map((act) => (
                                                <li key={act.id} className="mb-6 ml-6">
                                                    <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white dark:ring-[#1a2634] ${act.type === 'planning' ? 'bg-blue-100 text-blue-600' :
                                                        act.type === 'quality' ? 'bg-orange-100 text-orange-600' :
                                                            act.type === 'payments' ? 'bg-emerald-100 text-emerald-600' :
                                                                'bg-slate-100 text-slate-600'
                                                        }`}>
                                                        <span className="material-symbols-outlined text-[14px]">
                                                            {act.type === 'planning' ? 'calendar_month' :
                                                                act.type === 'quality' ? 'fact_check' :
                                                                    act.type === 'payments' ? 'handshake' : 'notifications'}
                                                        </span>
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-sm font-semibold text-slate-800 dark:text-white leading-tight">{act.title}</h3>
                                                        {(act.count || 1) > 1 && (
                                                            <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                                                x{act.count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <time className="mb-1 block text-[10px] font-normal leading-none text-slate-400 uppercase mt-1">
                                                        {new Date(act.timestamp).toLocaleString('es-CL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                                                    </time>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{act.description}</p>
                                                    <p className="text-[10px] text-slate-400 mt-1 font-medium">Por: {act.user}</p>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default DashboardScreen;